// src/pages/crm/ViewLeadD.js
import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import Header from "../../components/Header";
import API_BASE_URL from "../../config";

const ViewLeadD = () => {
  const { id } = useParams();
  const [lead, setLead] = useState(null);
  const [activeSection, setActiveSection] = useState("business");

  useEffect(() => {
    document.title = "Lead Details | Webyappy";
    fetchLeadDetails();
  }, []);

  const fetchLeadDetails = async () => {
    try {
      const response = await fetch(
        `${API_BASE_URL}/crm/get_lead_details/${id}`,
      );
      const result = await response.json();
      if (result.success) {
        setLead(result.data);
      }
    } catch (error) {
      console.error("Error fetching lead details:", error);
    }
  };

  useEffect(() => {
    const handleScroll = () => {
      const sections = ["business", "interactions"];
      for (const id of sections) {
        const el = document.getElementById(id);
        if (el) {
          const rect = el.getBoundingClientRect();
          if (rect.top <= 150 && rect.bottom >= 150) {
            setActiveSection(id);
            break;
          }
        }
      }
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  if (!lead) {
    return (
      <div className="text-center py-10">
        <div className="spinner-border text-primary" role="status" />
      </div>
    );
  }

  return (
    <>
      <Header />
      <div style={{ paddingTop: "70px", paddingBottom: "70px" }}>
        <div className="container-fluid py-3 px-3">
          <div className="d-flex align-items-center justify-content-between flex-wrap flex-sm-nowrap mb-2">
            {/* Left: Breadcrumb */}
            <ul className="breadcrumb breadcrumb-transparent breadcrumb-dot font-weight-bold p-0 mb-0">
              <li className="breadcrumb-item">
                <Link to="/home" className="text-muted">
                  Home
                </Link>
              </li>
              <li className="breadcrumb-item">
                <Link to="/pages/crm/viewleads" className="text-muted">
                  View Leads
                </Link>
              </li>
              <li className="breadcrumb-item">
                <span className="text-dark font-weight-bold">Lead Details</span>
              </li>
            </ul>

            {/* Right: Back Button */}
            <div className="mt-3 mt-sm-0">
              <Link to="/pages/crm/viewleads" className="btn btn-light">
                <i className="fas fa-arrow-left"></i> Back
              </Link>
            </div>
          </div>

          {/* Card */}
          <div className="row">
            {/* LEFT: Profile Summary */}
            <div className="col-lg-3 col-12 mb-4">
              <div className="card card-custom gutter-b shadow-sm">
                <div className="card-body pt-4 p-0">
                  {/* Profile Summary */}
                  <div className="text-center px-5 py-6 border-bottom">
                    {lead.profile_pic ? (
                      <img
                        src={`${API_BASE_URL.replace("/index.php", "")}/${lead.profile_pic}`}
                        alt={lead.full_name}
                        className="rounded-circle mb-3"
                        style={{
                          width: "60px",
                          height: "60px",
                          objectFit: "cover",
                          border: "3px solid #ccc",
                        }}
                      />
                    ) : (
                      <div className="symbol symbol-80 symbol-circle bg-light-primary mb-3 mx-auto d-flex align-items-center justify-content-center">
                        <span
                          className="symbol-label rounded-circle text-white font-weight-bold d-flex align-items-center justify-content-center"
                          style={{
                            width: "50px",
                            height: "50px",
                            backgroundColor: "#a18dfa",
                            fontSize: "18px",
                          }}
                        >
                          {lead.full_name?.charAt(0).toUpperCase()}
                        </span>
                      </div>
                    )}

                    <h5 className="font-weight-bolder text-primary mb-1">
                      {lead.full_name}
                    </h5>
                    <div className="text-dark mb-1">{lead.designation}</div>
                    <div className="font-weight-bold text-dark">
                      {lead.company}
                    </div>

                    {/* Funnel Stage + Priority */}
                    <div className="d-flex align-items-center justify-content-center flex-wrap mt-3">
                      {lead.funnel_stage && (
                        <span
                          className="badge badge-info font-weight-bold mr-2 mb-2 px-3 py-2"
                          style={{ fontSize: "12px", marginRight: "10px" }}
                        >
                          <i className="fas fa-filter mr-1"></i>{" "}
                          {lead.funnel_stage}
                        </span>
                      )}
                      {lead.priority && (
                        <span
                          className="badge badge-danger font-weight-bold mb-2 px-3 py-2"
                          style={{ fontSize: "12px" }}
                        >
                          {lead.priority} Priority
                        </span>
                      )}
                    </div>
                  </div>

                  {/* Quick Navigation */}
                  <div className="navi navi-bold navi-hover navi-active navi-link-rounded d-lg-block d-md-block d-none px-2 pt-3">
                    {/* Lead Info */}
                    <div className="navi-item mb-2">
                      <button
                        onClick={() =>
                          document
                            .getElementById("business")
                            ?.scrollIntoView({ behavior: "smooth" })
                        }
                        className={`scrollLink navi-link py-2 btn btn-link text-left w-100 ${activeSection === "business" ? "active" : ""}`}
                      >
                        <span className="navi-icon mr-2">
                          <i className="fas fa-building icon-lg text-primary"></i>
                        </span>
                        <span className="navi-text font-size-lg">
                          Lead Info
                        </span>
                      </button>
                    </div>

                    {/* Interactions */}
                    <div className="navi-item mb-2">
                      <button
                        onClick={() =>
                          document
                            .getElementById("interactions")
                            ?.scrollIntoView({ behavior: "smooth" })
                        }
                        className={`scrollLink navi-link py-2 btn btn-link text-left w-100 ${activeSection === "interactions" ? "active" : ""}`}
                      >
                        <span className="navi-icon mr-2">
                          <i className="fas fa-comments icon-lg text-primary"></i>
                        </span>
                        <span className="navi-text font-size-lg">
                          Interactions
                        </span>
                      </button>
                    </div>

                    {/* Proforma Invoice */}
                    <div className="navi-item mb-2">
                      <button
                        onClick={() =>
                          document
                            .getElementById("proformainvoice")
                            ?.scrollIntoView({ behavior: "smooth" })
                        }
                        className={`scrollLink navi-link py-2 btn btn-link text-left w-100 ${activeSection === "interactions" ? "active" : ""}`}
                      >
                        <span className="navi-icon mr-2">
                          <i className="far fa-file-alt icon-lg text-primary"></i>
                        </span>
                        <span className="navi-text font-size-lg">
                          Proforma Invoices
                        </span>
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* RIGHT: Lead Info */}
            <div className="col-lg-9 col-12">
              <div className="card shadow-sm p-3" id="business">
                <div className="border-bottom">
                  <h5 className="font-weight-bold  text-dark">Lead Info</h5>
                </div>

                <div className="row mt-3">
                  {/* Mobile & Email already shown above */}

                  {/* Address Grouped */}
                  {(lead.address ||
                    lead.city ||
                    lead.state ||
                    lead.pincode ||
                    lead.landmark) && (
                    <div className="col-md-6 mb-2">
                      <i className="fas fa-map-marker-alt text-primary mr-2" style={{ marginRight: "10px" }}></i>
                      {lead.address}
                      {lead.landmark && `, Near ${lead.landmark}`}
                      {lead.city && `, ${lead.city}`}
                      {lead.state && `, ${lead.state}`}
                      {lead.pincode && ` - ${lead.pincode}`}
                    </div>
                  )}

                  {/* Gender */}
                  {lead.gender && (
                    <div className="col-md-6 mb-2">
                      <i className="fas fa-venus-mars text-primary mr-2" style={{ marginRight: "10px" }}></i>
                      Gender: {lead.gender}
                    </div>
                  )}

                  {/* GST Number */}
                  {lead.gst_number && (
                    <div className="col-md-6 mb-2">
                      <i className="fas fa-receipt text-primary mr-2" style={{ marginRight: "10px" }}></i>
                      GST No.: {lead.gst_number}
                    </div>
                  )}

                  {/* Source */}
                  {lead.source && (
                    <div className="col-md-6 mb-2">
                      <i className="fas fa-bullhorn text-primary mr-2" style={{ marginRight: "10px" }}></i>
                      Source: {lead.source}
                    </div>
                  )}

                  {/* Business Category */}
                  {lead.business_category && (
                    <div className="col-md-6 mb-2">
                      <i className="fas fa-briefcase text-primary mr-2" style={{ marginRight: "10px" }}></i>
                      Business Category: {lead.business_category}
                    </div>
                  )}

                  {/* Website */}
                  {lead.website && (
                    <div className="col-md-6 mb-2">
                      <i className="fas fa-globe text-primary mr-2" style={{ marginRight: "10px" }}></i>
                      {lead.website}
                    </div>
                  )}

                  {/* LinkedIn */}
                  {lead.linkedin && (
                    <div className="col-md-6 mb-2">
                      <i className="fab fa-linkedin text-primary mr-2" style={{ marginRight: "10px" }}></i>
                      {lead.linkedin}
                    </div>
                  )}

                  {/* Assigned To */}
                  <div className="col-md-6 mb-2">
                    <i className="fas fa-user text-primary mr-2" style={{ marginRight: "10px" }}></i>
                    Assigned To (User ID):{" "}
                    <strong>{lead.assigned_to_name || "Not Assigned"}</strong>
                  </div>

                  {/* Created By */}
                  <div className="col-md-6 mb-2">
                    <i className="fas fa-user-plus text-primary mr-2" style={{ marginRight: "10px" }}></i>
                    <span
                      className={`font-weight-bold ${!lead.created_by_name ? "text-muted" : "text-dark"}`}
                    >
                      {lead.created_by_name || "Unknown"}
                    </span>
                    {lead.created_on && (
                      <>
                        &nbsp;|&nbsp;
                        {new Date(lead.created_on).toLocaleDateString("en-GB", {
                          day: "2-digit",
                          month: "short",
                          year: "numeric",
                        })}{" "}
                        {new Date(lead.created_on).toLocaleTimeString("en-IN", {
                          hour: "2-digit",
                          minute: "2-digit",
                        })}
                      </>
                    )}
                  </div>
                </div>
              </div>
              <div className="card shadow-sm p-3 mt-3" id="interactions">
                <div className="border-bottom">
                  <h5 className="font-weight-bold  text-dark">Interactions</h5>
                </div>
                <div className="p-2 mt-2">
                    <h6 className="text-muted">No Data Available</h6>
                </div>
              </div>
              <div className="card shadow-sm p-3 mt-3" id="proformainvoice">
                <div className="border-bottom">
                  <h5 className="font-weight-bold  text-dark">Proforma Invoices</h5>
                </div>
                <div className="p-2 mt-2">
                    <h6 className="text-muted">No Data Available</h6>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default ViewLeadD;
